import React from 'react'
import { Button, message, Skeleton, Tabs, Tooltip } from 'antd'
import { FormComponent } from '../../BaseComponent'
import { QuestionContextData } from '../../../context/QuestionContext'
import { LanguageContextData } from '../../../context/LanguageContext'
import { QuestionGroupContextData } from '../../../context/QuestionGroupContext'
import IQuestion from '../../../interfaces/IQuestion'
import ITranslationQuestion from '../../../interfaces/ITranslationQuestion'
import QuestionMain from './QuestionMain'
import QuestionTranslation from './QuestionTranslation'
import { QuestionSaveRequest, QuestionTranslationSaveRequest } from '../../../services/api/interfaces/ApiRequest'

type QuestionFormProps = {
  uuid?: string,
  permissions: any,
  
  history: any,

  questionContext: QuestionContextData,
  languageContext: LanguageContextData,
  questionGroupContext: QuestionGroupContextData
}

export default class QuestionForm extends FormComponent<QuestionFormProps> {

  state = {
    loaded: false,
    saving: false,
    activeKey: 'question_main',

    translation: [] as ITranslationQuestion[]
  }

  async componentDidMount() {
    const { questionGroupContext, languageContext } = this.props

    if (!questionGroupContext.questionGroups) {
      await questionGroupContext.requestQuestionGroupList({
        options: {
          limit:9999,
          _force_ids: true
        }
      })
    }

    if (!languageContext.languages) {
      await languageContext.requestLanguageList({
        options: {
          limit:9999,
          _force_ids: true
        }
      })
    }
    
    const question = await this.load()

    this.setState({
      loaded: true,
      translation: question ? question.translation : []
    })
  }

  private async load(): Promise<IQuestion | null> {
    const { uuid, questionContext } = this.props
    if (!uuid) return null

    const question = await questionContext.requestQuestionFind({
      search: {
        uuid,
        translation: {
          with: {
            language: {
              with: true
            }
          }
        }
      },
      options: {
        _force_ids: true
      }
    })

    await questionContext.requestQuestionGetAnswers({
      question_uuid: uuid
    })

    return question
  }

  handleTabsChange = (activeKey: string) => {
    this.setState({
      activeKey
    })
  }

  handleAddTranslation = () => {
    const { translation } = this.state
    const fakeUuid = `fake_${Date.now()}`

    const newTranslation: ITranslationQuestion = {
      uuid: fakeUuid,
      ask: '',
      id: 0,
      language_id: 0,
      parent_id: 0,
    }

    translation.push(newTranslation)
    this.setState({
      activeKey: `question_translation_${fakeUuid}`,
      translation
    })
  }

  handleSave = async (values: any) => {
    const { uuid, questionContext } = this.props

    try {
      let columnTypeKey = 'TEXT'
      if (['CHECKBOX', 'RADIO', 'SELECT'].includes(values.form_tag_key)) {
        columnTypeKey = 'ID'
      }

      if (values.form_tag_key === 'RANGE') {
        columnTypeKey = 'INTEGER'
      }

      const payload: QuestionSaveRequest = {
        organization_question: {
          ask: values.ask,
          column_type_key: columnTypeKey,
          form_tag_key: values.form_tag_key,
          group_id: values.group_id,
          position: values.position,
        },
        answers: values.answers?.map(answer => {
          return {
            uuid: answer.uuid || null,
            value: answer.value,
            question_id: answer.question_id,
            position: answer.position,
            cycle: answer.cycle || null
          }
        })
      }

      if (uuid) {
        payload.organization_question.uuid = uuid
      }

      this.setState({
        saving: true
      })

      const quesstion = await questionContext.requestQuestionSave(payload)

      if (uuid) {
        await this.load()
      } else {
        this.props.history.replace(`/organization/question/${quesstion.uuid}`)
      }

      message.success('Question saved successfully!')
    } catch (error) {
      message.error(`Failed to save question! Reason: ${error}`)
    } finally {
      this.setState({
        saving: false
      })
    }
  }

  handleTranslationSave = async (values: any) => {
    const { uuid, questionContext } = this.props
    if (!uuid) return message.error('Invalid question')

    try {
      const answers: any = []
      questionContext.answers?.forEach(v => {
        const valueAnswer = values?.answers ? values?.answers[v.uuid] : undefined

        if (valueAnswer) {
          answers.push({
            uuid: v.uuid,
            value: valueAnswer.value
          })
        }
      })

      const payload: QuestionTranslationSaveRequest = {
        language_uuid: values.language_uuid,
        question: {
          uuid,
          answers,
          ask: values.ask,
        }
      }

      this.setState({
        saving: true
      })

      await questionContext.requestQuestionTranslationSave(payload)
      const question = await this.load()

      const currentTranslation = question?.translation?.find(v => v.language?.uuid === values.language_uuid)

      this.setState({
        activeKey: `question_translation_${currentTranslation?.uuid || values.uuid}`,
        translation: question?.translation || []
      })
      message.success('Question translation saved successfully!')
    } catch (error) {
      message.error(`Could not save question translation. Reason: ${error}`)
    } finally {
      this.setState({
        saving: false
      })
    }
  }

  handleTranslationDelete = async (translationUuid: string, languageUuid?: string) => {
    const { uuid, questionContext } = this.props
    let { translation } = this.state

    try {
       // Unsaved yet? So, we do not need to make any request :)
      if (translationUuid.includes('fake')) {
        translation.splice(translation.findIndex(v => v.uuid === translationUuid))
      }

      if (!translationUuid.includes('fake') && uuid && languageUuid) {
        await questionContext.requestQuestionTranslationDelete({
          language_uuid: languageUuid,
          question_uuid: uuid
        })
        
        const question = await this.load()
        translation = question?.translation || []
      }

      this.setState({
        activeKey: 'question_main',
        translation
      }, () => {
        message.success('Translation removed successfully!')
      })
    } catch (error) {
      message.error(`Failed to remove translation. Reason: ${error}`)
    }
  }

  _renderAdd = () => {
    if (!this.props.uuid) return null

    return (
      <Tooltip
        title="Add new translation to question"
        placement="left"
      >
        <Button type="primary" shape="round" size="small" onClick={this.handleAddTranslation}>
          Add new translation to question
        </Button>
      </Tooltip>
    )
  }

  render() {
    const { uuid, questionContext, questionGroupContext, languageContext } = this.props
    const { loaded, saving, activeKey, translation } = this.state

    if (!loaded) {
      return <Skeleton />
    }

    const languages = languageContext.languages || []

    let values: any = {}

    if (uuid && questionContext.question && questionContext.answers) {
      values = {
        ...questionContext.question,
        answers: questionContext.answers
      }
    }

    return (
      <Tabs activeKey={activeKey} tabBarExtraContent={this._renderAdd()} onChange={this.handleTabsChange}>
        <Tabs.TabPane key="question_main" tab="Main">
          <QuestionMain
            values={values}
            groups={questionGroupContext.questionGroups || []}

            loading={!!questionContext.fetching}
            onSave={this.handleSave}
          />
        </Tabs.TabPane>

        {
          translation.map(v => {
            const tabName = v.language?.name || '* Unsaved'

            return (
              <Tabs.TabPane key={`question_translation_${v.uuid}`} tab={tabName}>
                {
                  !saving ? (
                    <QuestionTranslation
                      questionTranslation={v}
                      languages={languages}
                      answers={questionContext.answers || []}
                      formTag={questionContext.question?.form_tag_key || ''}
                      loading={!!questionContext.fetching}
                      onSave={(values) => this.handleTranslationSave(values)}
                      onDelete={this.handleTranslationDelete}
                    />
                  ) : <Skeleton />
                }
              </Tabs.TabPane>
            )
          })
        }
      </Tabs>
    )
  }
}
